import React, { useState, useEffect, useMemo, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaAward,
  FaTimes,
  FaQuoteLeft,
  FaShieldAlt,
  FaBrain,
  FaInfinity,
  FaBullseye,
  FaCheck,
  FaArrowRight,
  FaSpinner,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { AuthContext } from "../../../context/AuthContext";
import { swalError, swalSuccess } from "../../../utils/swalAlert";
import { fetchWithAuth } from "../../../utils/api";
import { isQualificationDateActive, parseFlexibleDate } from "./Qualified";
import "./Announcement.css";

export { isQualificationDateActive, parseFlexibleDate };

const serverUrl = process.env.REACT_APP_SERVER_URL;

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function NotQualified({
  forceOpen = false,
  onCloseCallback = null,
  onViewPoolCallback = null,
  poolClassId = null,
  classes = null,
  userName = "",
}) {
  const navigate = useNavigate();
  const { user: authUser, setUser } = useContext(AuthContext) || {};
  const isDateEligible = useMemo(() => isQualificationDateActive(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [internalClasses, setInternalClasses] = useState([]);

  // Fetch enrolled classes if not provided via props
  useEffect(() => {
    if (poolClassId || (classes && classes.length > 0) || !authUser?._id) {
      return;
    }
    const fetchEnrolledClasses = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/enrollment/get-all/student/${authUser._id}`,
          { credentials: "include" }
        );
        if (response && response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setInternalClasses(data);
          }
        }
      } catch (err) {
        // Fallback gracefully
      }
    };
    fetchEnrolledClasses();
  }, [poolClassId, classes, authUser?._id]);

  // Determine target classId:
  // First class that is not practice (isPracticeZone or isPractice)
  const targetClassId = useMemo(() => {
    if (poolClassId) return poolClassId;
    const list = classes && classes.length > 0 ? classes : internalClasses;
    if (!list || !list.length) return null;

    const isPracticeClass = (cls) => {
      if (!cls) return false;
      const c = cls.class || cls;
      return (
        c.isPracticeZone === true ||
        c.isPractice === true ||
        c.name === "Practice Zone" ||
        cls.isPracticeZone === true ||
        cls.isPractice === true
      );
    };

    const nonPractice = list.filter((cls) => !isPracticeClass(cls));
    const target = nonPractice[0] || list[0];
    return (
      target?.classId ||
      target?.class?._id ||
      target?.class?.id ||
      target?._id ||
      target?.id ||
      null
    );
  }, [poolClassId, classes, internalClasses]);

  const handleNavigateToClass = () => {
    handleClose();
    if (onViewPoolCallback) {
      onViewPoolCallback();
    } else if (targetClassId) {
      navigate(`/student/view-class/${targetClassId}`);
    } else {
      navigate("/student/classes");
    }
  };

  const actualPrice = parseInt(process.env.REACT_APP_ACTUAL_PRICE || "799", 10);
  const discountedPrice = parseInt(
    process.env.REACT_APP_DISCOUNTED_PRICES || "399",
    10
  );
  const discountPercent = Math.round(
    ((actualPrice - discountedPrice) / actualPrice) * 100
  );

  // Compute days remaining in free/current access
  const expiryDaysRemaining = useMemo(() => {
    const rawExpiry = authUser?.loginExpiryDate;
    if (!rawExpiry) return 0;
    const expDate = new Date(rawExpiry);
    const now = new Date();
    const diffTime = expDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, [authUser?.loginExpiryDate]);

  // Handle initial auto-open
  useEffect(() => {
    if (!isDateEligible && !forceOpen) return;

    if (forceOpen) {
      setIsOpen(true);
      return;
    }

    const dismissedAt = localStorage.getItem(
      "grademe_phase1_not_qualified_dismissed_at"
    );
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const hoursSinceDismissal = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissal < 24) {
        // Dismissed within last 24h -> keep modal closed initially, show floating badge
        return;
      }
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 700);

    return () => clearTimeout(timer);
  }, [isDateEligible, forceOpen]);

  // Close modal handler
  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    if (dontShowToday) {
      localStorage.setItem(
        "grademe_phase1_not_qualified_dismissed_at",
        new Date().toISOString()
      );
    }
    if (onCloseCallback) onCloseCallback();
  };

  // Quick re-open from floating trigger
  const handleOpen = () => {
    setIsOpen(true);
  };

  // Directly open Razorpay payment gateway
  const handleProceedPayment = async () => {
    try {
      setLoadingPayment(true);

      const loaded = await loadRazorpay();
      if (!loaded) {
        throw new Error("Unable to load Razorpay checkout SDK.");
      }

      const response = await fetch(`${serverUrl}/api/payments/create-order`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ amount: discountedPrice }),
      });

      const order = await response.json();
      if (!response.ok) {
        throw order;
      }

      const razorpay = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: "GradeMe AI",
        description: "1-year plan extension for JEE / NEET Prep",
        handler: async (paymentResponse) => {
          try {
            const verifyRes = await fetch(
              `${serverUrl}/api/payments/verify-payment`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify(paymentResponse),
              }
            );

            const result = await verifyRes.json();
            if (!verifyRes.ok) {
              throw result;
            }

            if (result.success) {
              if (setUser) {
                try {
                  const userRes = await fetch(`${serverUrl}/api/user/signed-in`, {
                    credentials: "include",
                    headers: { Accept: "application/json" },
                  });
                  if (userRes.ok) {
                    const freshUserData = await userRes.json();
                    if (freshUserData && freshUserData._id) {
                      const category =
                        freshUserData.category ||
                        freshUserData.institute?.category ||
                        "K12_SCHOOL";
                      setUser({ ...freshUserData, category });
                    }
                  }
                } catch (e) {
                  console.error("Error refreshing user context post-payment:", e);
                }
              }
              swalSuccess("Payment successful! Your 1-Year GradeMe AI Pro access is now active.");
              handleClose();
            } else {
              swalError(result.message || "Payment verification failed.");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            swalError(
              error.message ||
                "Payment verification failed. Please contact support if money was deducted."
            );
          } finally {
            setLoadingPayment(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoadingPayment(false);
          },
        },
        theme: {
          color: "#0f172a",
        },
      });

      razorpay.on("payment.failed", (res) => {
        console.error("Razorpay payment failed:", res.error);
        swalError(res.error?.description || "Payment failed. Please try again.");
        setLoadingPayment(false);
      });

      razorpay.open();
    } catch (error) {
      console.error("Payment error:", error);
      swalError(error.message || "Unable to start payment. Please try again.");
      setLoadingPayment(false);
    }
  };

  // Product benefits
  const proBenefits = [
    {
      icon: FaBullseye,
      colorClass: "nq-icon-orange",
      title: "Real Exam-Mirroring Practice",
      desc: "Authentic questions mirroring actual JEE / NEET exam difficulty.",
    },
    {
      icon: FaBrain,
      colorClass: "nq-icon-purple",
      title: "AI Weak Spot Diagnosis",
      desc: "Pinpoint exactly where you lose marks and fix concepts instantly.",
    },
    {
      icon: FaInfinity,
      colorClass: "nq-icon-blue",
      title: "Unlimited Full Mock Tests",
      desc: "Simulate real exam pressure to turn anxiety into unstoppable confidence.",
    },
    {
      icon: FaShieldAlt,
      colorClass: "nq-icon-green",
      title: "365 Days Complete AI Prep",
      desc: "Uninterrupted guidance with verified step-by-step solutions all year.",
    },
  ];

  // If outside active date range and not forced open, render nothing
  if (!isDateEligible && !forceOpen) {
    return null;
  }

  return (
    <>
      {/* Floating Interactive Trigger Pill (Shown when modal is closed) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="p1-floating-trigger-container"
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 30 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <motion.button
              className="p1-floating-pill p1-pill-not-qualified"
              onClick={handleOpen}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              aria-label="View Phase 1 Results & Certificate"
            >
              <span className="p1-floating-pulse-ring nq-pulse-ring"></span>
              <span className="p1-floating-icon p1-icon-blue">
                <FaAward />
              </span>
              <div className="p1-floating-text">
                <span className="p1-floating-title">Phase 1 Results Declared</span>
                <span className="p1-floating-sub">Download Certificate</span>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Animated Modal Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="p1-modal-backdrop-wrapper">
            <motion.div
              className="p1-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />

            <div className="p1-modal-center-container nq-modal-container">
              <motion.div
                className="p1-modal-card nq-modal-card"
                initial={{ opacity: 0, scale: 0.88, y: 40 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: { type: "spring", stiffness: 320, damping: 28 },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.88,
                  y: 40,
                  transition: { duration: 0.22 },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Ambient Decorative Glows */}
                <div className="p1-glow-top-left nq-glow-orange"></div>
                <div className="p1-glow-bottom-right nq-glow-purple"></div>

                {/* Modal Close Button */}
                <motion.button
                  className="p1-close-btn"
                  onClick={handleClose}
                  whileHover={{ scale: 1.15, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Close Announcement"
                >
                  <FaTimes />
                </motion.button>

                {/* Top Encouragement Badge */}
                <div className="nq-badge-header">
                  <span className="nq-tag-pill">
                    <HiSparkles /> Phase 1 Results Declared
                  </span>
                  <span className="nq-cert-pill">
                    <FaAward /> Participation Certificate Ready
                  </span>
                </div>

                {/* Encouraging Headline */}
                <div className="nq-header-text">
                  <h2 className="nq-title">
                    You Won't Be Moving Forward to Phase 2{userName ? `, ${userName}` : ""}!
                  </h2>
                  <p className="nq-subtitle">
                    The competition this round was exceptionally tough, and simply making it this far says a lot about you. We hope to see you again next time.
                  </p>
                </div>

                {/* Certificate & Encouragement Banner */}
                <motion.div
                  className="p1-message-banner-box nq-message-banner"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <div className="p1-msg-content-wrapper">
                    <div className="p1-msg-quote-icon nq-quote-icon" aria-hidden="true">
                      <FaQuoteLeft />
                    </div>
                    <div className="p1-msg-paragraphs">
                      <p className="p1-msg-p1">
                        <strong>Your Participation Certificate is Available for Download!</strong>
                      </p>
                      <p className="p1-msg-p2">
                        Head over to your Phase 1 exam to download your verified certificate of participation.
                      </p>
                      <div className="nq-cert-action-row">
                        <motion.button
                          className="nq-btn-cert"
                          onClick={handleNavigateToClass}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <FaAward />
                          <span>Go to My Exam & Download Certificate</span>
                          <FaArrowRight />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Upsell / Sales Section */}
                <div className="nq-upsell-section">
                  <div className="nq-upsell-header">
                    <div className="nq-upsell-badge">
                      <HiSparkles /> UDGHOSH Student Offer
                    </div>
                    <h3 className="nq-upsell-title">
                      Your Preparation Shouldn't Stop Here! 🚀
                    </h3>
                    <p className="nq-upsell-sub">
                      Turn your weak spots into your greatest strength with <strong>GradeMe AI Pro</strong>.
                    </p>
                  </div>

                  {/* 2x2 Benefits Grid */}
                  <div className="nq-benefits-grid">
                    {proBenefits.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <motion.div
                          key={index}
                          className="nq-benefit-item"
                          whileHover={{ y: -2, scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 350 }}
                        >
                          <div className={`nq-benefit-icon ${item.colorClass}`}>
                            <IconComponent />
                          </div>
                          <div className="nq-benefit-info">
                            <h4>{item.title}</h4>
                            <p>{item.desc}</p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Pricing Box & CTA Card */}
                  <div className="nq-pricing-box">
                    <div className="nq-pricing-left">
                      <div className="nq-plan-title-row">
                        <span className="nq-plan-name">Annual Preparation Pass</span>
                        <span className="nq-discount-tag">
                          Save {discountPercent}%
                        </span>
                      </div>
                      <div className="nq-price-display">
                        <span className="nq-price-old">₹{actualPrice}</span>
                        <span className="nq-price-current">₹{discountedPrice}</span>
                        <span className="nq-price-period">/ full year</span>
                      </div>
                      <div className="nq-perks-list">
                        <span>
                          <FaCheck /> Unlimited Mock Tests & Analytics
                        </span>
                        <span>
                          <FaCheck /> Instant 365 Days Activation
                        </span>
                      </div>
                    </div>

                    <div className="nq-pricing-right">
                      <motion.button
                        className="nq-cta-btn"
                        onClick={handleProceedPayment}
                        disabled={loadingPayment}
                        whileHover={!loadingPayment ? { scale: 1.03, y: -2 } : {}}
                        whileTap={!loadingPayment ? { scale: 0.97 } : {}}
                      >
                        {loadingPayment ? (
                          <>
                            <FaSpinner className="fa-spin" />
                            <span>Opening Payment Gateway...</span>
                          </>
                        ) : (
                          <>
                            <span>Upgrade My Prep</span>
                            <FaArrowRight />
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Footer Dismiss / Keep Practicing Controls */}
                <div className="p1-action-footer nq-action-footer">
                  <div className="p1-dismiss-row">
                    <label className="p1-checkbox-label">
                      <input
                        type="checkbox"
                        checked={dontShowToday}
                        onChange={(e) => setDontShowToday(e.target.checked)}
                      />
                      <span>Don't auto-popup again today</span>
                    </label>

                    <button
                      className="nq-btn-secondary"
                      onClick={handleClose}
                      type="button"
                    >
                      Continue with Free Practice {expiryDaysRemaining > 0 ? `(${expiryDaysRemaining} days left)` : ""}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
